/**
 * Helper class to work with a Shopify form
 *
 * @typedef {"success"} Events
 */
class ShopifyFormHelper {
	/**
	 *
	 * @param {HTMLElement} formSection
	 * @param {(formSection: HTMLElement) => HTMLElement | null} getShopifyFormRootCallback
	 * @returns {Promise<ShopifyFormHelper>}
	 */
	static waitTillShopifyFormIsReady(formSection, getShopifyFormRootCallback) {
		return new Promise((resolve) => {
			const onDomChange = () => {
				const formEmbed = getShopifyFormRootCallback(formSection);
				if (formEmbed === null) {
					return;
				}
				observer.disconnect();
				const instance = new ShopifyFormHelper(formEmbed);
				resolve(instance);
			};
			const observer = new MutationObserver(onDomChange);

			observer.observe(formSection, { childList: true, subtree: true });
			onDomChange();
		});
	}

	/**
	 * @param {HTMLElement} formEmbed
	 */
	constructor(formEmbed) {
		this.formEmbed = formEmbed;
		this.root = formEmbed.shadowRoot;
		/** @type {{[key in Events]: Set<() =>void>}} */
		this.callbackList = {
			success: new Set(),
		};

		/** @type {"success" | "form" | "error"} */
		this.state = "form";

		if (this.root === null) {
			throw new Error("Could not find Shopify Form shadowRoot");
		}

		this._detectFormChange();
	}

	_init() {}

	_getForm() {
		return this.root.querySelector('[data-testid="form"]') ?? this.root.querySelector("form");
	}

	_getSuccessBanner() {
		return this.root.querySelector('[data-testid="banner-success"]');
	}

	_detectFormChange() {
		const observer = new MutationObserver(() => {
			const form = this._getForm();
			const successBanner = this._getSuccessBanner();
			if (form === null && successBanner !== null) {
				if (this.state !== "success") {
					this.state = "success";
					this._triggerEvents("success");
				}
			} else if (form !== null) {
				if (this.state !== "form") {
					this.state = "form";
				}
			} else {
				if (this.state !== "error") {
					this.state = "error";
				}
			}
		});

		observer.observe(this.root, { attributes: true, childList: true, subtree: true });
	}

	/**
	 * @param {Events} event
	 */
	_triggerEvents(event) {
		if (this.callbackList[event].size <= 0) {
			return;
		}

		this.callbackList[event].forEach((callback) => callback());
	}

	/**
	 * @param {Events} event
	 * @param {() => void} callback
	 */
	on(event, callback) {
		this.callbackList[event].add(callback);
	}

	submitForm() {
		const form = this._getForm();
		if (form === null) {
			throw new Error("Could not find form element in Shopify form");
		}
		form.requestSubmit ? form.requestSubmit() : form.submit();
	}

	querySelector(selector) {
		return this.root.querySelector(selector);
	}
}
